/**
 * XSS Protection Mixin
 * Provides safe HTML sanitization for v-html directives
 */
import DOMPurify from 'dompurify';

// Configure DOMPurify with safe defaults
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'object', 'embed'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
};

// Force all links to open in new tab for safety
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export default {
  methods: {
    /**
     * Sanitize HTML string to prevent XSS attacks
     * @param {string} html - The HTML string to sanitize
     * @returns {string} - Sanitized HTML safe for v-html
     */
    sanitizeHtml(html) {
      if (!html) return '';
      return DOMPurify.sanitize(html, DOMPURIFY_CONFIG);
    },

    /**
     * Sanitize HTML with custom allowed tags
     * @param {string} html - The HTML string to sanitize
     * @param {string[]} allowedTags - Additional tags to allow
     * @returns {string} - Sanitized HTML
     */
    sanitizeHtmlWithTags(html, allowedTags = []) {
      if (!html) return '';
      const config = {
        ...DOMPURIFY_CONFIG,
        ALLOWED_TAGS: [...DOMPURIFY_CONFIG.ALLOWED_TAGS, ...allowedTags]
      };
      return DOMPurify.sanitize(html, config);
    },

    /**
     * Strip all HTML tags, returning only text content
     * @param {string} html - The HTML string to strip
     * @returns {string} - Plain text without HTML
     */
    stripHtml(html) {
      if (!html) return '';
      return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
    },

    /**
     * Highlight search term in text safely (escapes HTML first)
     * @param {string} text - The text to search in
     * @param {string} searchTerm - The term to highlight
     * @returns {string} - HTML with highlighted term
     */
    safeHighlight(text, searchTerm) {
      if (!text || !searchTerm) return this.escapeHtml(text || '');

      // First escape the text to prevent XSS
      const escapedText = this.escapeHtml(text);
      const escapedSearchTerm = this.escapeHtml(searchTerm);

      // Create regex from escaped search term
      const regex = new RegExp(`(${escapedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

      // Replace matches with bold tags
      return escapedText.replace(regex, '<b>$1</b>');
    },

    /**
     * Escape HTML special characters
     * @param {string} text - The text to escape
     * @returns {string} - Escaped text safe for HTML
     */
    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }
};
